"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.REQUIRED_REGIONS_BY_METRIC = void 0;
const skin_1 = require("./skin");
/**
 * Shared empty-region constant for face-level metrics that have no
 * placeholder dots (skin_type / skintone / ita). Hoisted so the entries
 * below can reference a typed `readonly Area[]` value instead of an
 * inline `[]` that would widen to `never[]` under the `satisfies`
 * clause and force a cast.
 */
const FACE_LEVEL_NO_PLACEHOLDERS = [];
/**
 * Maps each skin metric to the facial regions Stella analyzes for it.
 *
 * Source of truth for the FRONTEND. Backend has its own copy at
 * `apps/backend/functions/src/types/scan/regionMappings.ts` that reuses
 * the per-group region arrays from the Stage2 prompt schemas. The two
 * must stay in sync — when adding/removing a metric or shifting its
 * regions, update both. (A future cleanup can pull this in as the
 * single source for both packages.)
 *
 * Used to render placeholder landmark markers during the paywall
 * report's progressive reveal: while regional analysis is still
 * processing (and `regionalData` is empty), we show pulsing
 * placeholders at the default positions for each metric's expected
 * regions, so the user sees "Stella is looking at your forehead and
 * cheeks" before the real scored markers come in.
 */
exports.REQUIRED_REGIONS_BY_METRIC = {
    // Group 1: Skin Sensitivity
    [skin_1.SkinMetricTechName.hydration]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.chin,
        skin_1.SkinMetricAreaName.left_eye,
        skin_1.SkinMetricAreaName.right_eye,
    ],
    [skin_1.SkinMetricTechName.texture_roughness]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.chin,
    ],
    [skin_1.SkinMetricTechName.redness]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
        skin_1.SkinMetricAreaName.chin,
    ],
    [skin_1.SkinMetricTechName.surface_sebum]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.chin,
    ],
    [skin_1.SkinMetricTechName.telangiectasia]: [
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
        skin_1.SkinMetricAreaName.forehead,
    ],
    // Group 2: Clarity & Breakouts
    [skin_1.SkinMetricTechName.breakouts]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.chin,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
    ],
    [skin_1.SkinMetricTechName.pores]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.chin,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
    ],
    [skin_1.SkinMetricTechName.pie]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.chin,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
    ],
    // Group 3: Tone Evenness — pigmentation family shares the same regions
    [skin_1.SkinMetricTechName.pigmentation]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.chin,
    ],
    [skin_1.SkinMetricTechName.sun_spots]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.chin,
    ],
    [skin_1.SkinMetricTechName.melasma]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.chin,
    ],
    [skin_1.SkinMetricTechName.freckles]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.chin,
    ],
    // Group 4: Structure & Aging — lines family shares regions
    [skin_1.SkinMetricTechName.lines]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.chin,
        skin_1.SkinMetricAreaName.left_eye,
        skin_1.SkinMetricAreaName.right_eye,
    ],
    [skin_1.SkinMetricTechName.fine_lines]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.chin,
        skin_1.SkinMetricAreaName.left_eye,
        skin_1.SkinMetricAreaName.right_eye,
    ],
    [skin_1.SkinMetricTechName.deep_lines]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.chin,
        skin_1.SkinMetricAreaName.left_eye,
        skin_1.SkinMetricAreaName.right_eye,
    ],
    [skin_1.SkinMetricTechName.nasolabial_wrinkles]: [skin_1.SkinMetricAreaName.perioral],
    [skin_1.SkinMetricTechName.marionette_lines]: [skin_1.SkinMetricAreaName.perioral],
    [skin_1.SkinMetricTechName.sagging]: [
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
        skin_1.SkinMetricAreaName.jawline,
        skin_1.SkinMetricAreaName.chin,
    ],
    [skin_1.SkinMetricTechName.jowls]: [
        skin_1.SkinMetricAreaName.jawline,
        skin_1.SkinMetricAreaName.chin,
    ],
    // Face-level age maps to JOWLS_REGIONS in the backend mapping.
    [skin_1.SkinMetricTechName.age]: [
        skin_1.SkinMetricAreaName.jawline,
        skin_1.SkinMetricAreaName.chin,
    ],
    // Group 5: Eye Area
    [skin_1.SkinMetricTechName.dark_circles]: [
        skin_1.SkinMetricAreaName.left_eye,
        skin_1.SkinMetricAreaName.right_eye,
    ],
    [skin_1.SkinMetricTechName.puffiness]: [
        skin_1.SkinMetricAreaName.left_eye,
        skin_1.SkinMetricAreaName.right_eye,
    ],
    [skin_1.SkinMetricTechName.eye_bags]: [
        skin_1.SkinMetricAreaName.left_eye,
        skin_1.SkinMetricAreaName.right_eye,
    ],
    [skin_1.SkinMetricTechName.lacrimal_grooves]: [
        skin_1.SkinMetricAreaName.left_eye,
        skin_1.SkinMetricAreaName.right_eye,
    ],
    [skin_1.SkinMetricTechName.eyes_age]: [
        skin_1.SkinMetricAreaName.left_eye,
        skin_1.SkinMetricAreaName.right_eye,
    ],
    // Group 6: Baseline Properties — face-level only.
    // Backend uses EYE_REGIONS as a *schema placeholder* here so the LLM
    // prompt validates, but the actual assessment isn't regional. For
    // frontend rendering we want zero placeholder dots, so we diverge
    // intentionally and return empty arrays. If backend rewires these to
    // a real region set, mirror it here.
    [skin_1.SkinMetricTechName.skin_type]: FACE_LEVEL_NO_PLACEHOLDERS,
    [skin_1.SkinMetricTechName.skintone]: FACE_LEVEL_NO_PLACEHOLDERS,
    [skin_1.SkinMetricTechName.ita]: FACE_LEVEL_NO_PLACEHOLDERS,
    // Deprecated metrics — keep entries so the Record type is total.
    // Each deprecated metric mirrors the same backend constant its
    // replacement uses, so the orderings match exactly.
    [skin_1.SkinMetricTechName.enlarged_pores]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.chin,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
    ],
    [skin_1.SkinMetricTechName.inflammation]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
        skin_1.SkinMetricAreaName.chin,
    ],
    [skin_1.SkinMetricTechName.irritation]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
        skin_1.SkinMetricAreaName.chin,
    ],
    [skin_1.SkinMetricTechName.pimples]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.chin,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
    ],
    [skin_1.SkinMetricTechName.uniformness]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.chin,
    ],
    [skin_1.SkinMetricTechName.translucency]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.chin,
    ],
    [skin_1.SkinMetricTechName.moles]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.chin,
    ],
    [skin_1.SkinMetricTechName.pigmentation_composite]: [
        skin_1.SkinMetricAreaName.forehead,
        skin_1.SkinMetricAreaName.left_cheek,
        skin_1.SkinMetricAreaName.right_cheek,
        skin_1.SkinMetricAreaName.nose,
        skin_1.SkinMetricAreaName.upper_lip,
        skin_1.SkinMetricAreaName.chin,
    ],
};
//# sourceMappingURL=regionMappings.js.map